import { AddOutlined, AttachFileOutlined } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import "ag-charts-enterprise";
import { LicenseManager } from "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import isEmpty from "lodash/isEmpty";
import size from "lodash/size";

import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import LoadingFallback from "../../../components/LoadingFallback";
import TitledCard from "../../../components/ui/TitledCard";
import CategorizedLoanDocumentsTable from "../../../components/common/Loan/tables/CategorizedLoanDocumentsTable.js";
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_KEY);
// Lazy load heavy components
const LoanBulkFileUploader = lazy(() => import("./LoanBulkFileUploader"));
const LoanDocumentGroupIcon = lazy(() => import("./LoanDocumentGroupIcon"));

const LoanDocumentGroupFilesCard = ({
  title,
  docGroup,
  showIfEmpty,
  row,
  hideChart,
  showGroupIcon,
  // maxHeight,
  sx,
}) => {
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments
  );

  const filteredDocGroupDocuments = useMemo(() => {
    if (isEmpty(loanDocuments)) {
      return [];
    }
    return loanDocuments.filter((doc) => {
      return doc?.docGroup === docGroup && !doc?.isHidden;
    });
  }, [loanDocuments, docGroup]);

  const cardTitle = useMemo(() => {
    if (title) {
      return `${title} (${size(filteredDocGroupDocuments)})`;
    }
    return `${docGroup} (${size(filteredDocGroupDocuments)})`;
  }, [title, docGroup, filteredDocGroupDocuments]);

  if (isEmpty(filteredDocGroupDocuments) && !showIfEmpty) {
    return null;
  }

  return (
    <TitledCard
      variant="h4"
      title={cardTitle}
      cardSx={{
        flexGrow: 0,
        flexShrink: 0,
        h4: {
          fontSize: "13px",
          svg: {
            fontSize: "16px",
          },
        },
        ...sx,
      }}
      headerSx={{ p: 2, paddingBottom: "8px !important" }}
      bodySx={{
        display: "flex",
        flexDirection: row ? "row" : "column",
        gap: 2,
        p: 0,
        // maxHeight: maxHeight,
        // overflow: "auto",
      }}
      icon={
        showGroupIcon ? (
          <Suspense fallback={<LoadingFallback />}>
            <LoanDocumentGroupIcon docGroup={docGroup} />
          </Suspense>
        ) : (
          <AttachFileOutlined className="thin" />
        )
      }
      headerEndContent={<FileUploaderIconButton docGroup={docGroup} />}
    >
      {hideChart ? null : (
        <LoanDocumentsGroupFilesPieChart
          docGroup={docGroup}
          loanDocuments={filteredDocGroupDocuments}
        />
      )}
      <Suspense fallback={<LoadingFallback />}>
        <CategorizedLoanDocumentsTable
          groupByDocType
          // groupByDocGroup
          rowData={filteredDocGroupDocuments}
        />
      </Suspense>
      {/* <Suspense fallback={<LoadingFallback />}>
        <LoanDocumentsGroupFilesTable rowData={filteredDocGroupDocuments} />
      </Suspense> */}
    </TitledCard>
  );
};

const FileUploaderIconButton = ({ docGroup }) => {
  const [addFiles, setAddFiles] = useState(false);
  return (
    <>
      <IconButton
        onClick={() => setAddFiles(true)}
        sx={{ position: "absolute", right: 8, top: 8 }}
        size="small"
      >
        <AddOutlined />
      </IconButton>
      {addFiles && (
        <Suspense fallback={<LoadingFallback />}>
          <LoanBulkFileUploader
            show={addFiles}
            onClose={() => setAddFiles(false)}
            docGroup={docGroup}
          />
        </Suspense>
      )}
    </>
  );
};

const LoanDocumentsGroupFilesPieChart = ({ docGroup, loanDocuments }) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    // Filter documents by the specified docGroup if provided
    const filteredDocs = docGroup
      ? loanDocuments.filter((doc) => doc.docGroup === docGroup)
      : loanDocuments;

    // Count the number of files per docType
    const docTypeCounts = filteredDocs.reduce((acc, doc) => {
      const docType = doc.docType || "Unknown";
      acc[docType] = (acc[docType] || 0) + 1;
      return acc;
    }, {});

    // Convert to array format for AG Charts
    const chartDataArray = Object.entries(docTypeCounts).map(
      ([docType, count]) => ({
        docType,
        count,
      })
    );

    setChartData(chartDataArray);
  }, [loanDocuments, docGroup]);

  const chartOptions = useMemo(
    () => ({
      padding: {
        top: 16,
        right: 30,
        bottom: 16,
        left: 0,
      },
      height: 220,
      width: 480,
      // autoSize: false,
      legend: {
        enabled: true,
        position: "right",
        item: {
          paddingY: 15,
          marker: {
            shape: "circle",
            size: 10,
          },
        },
      },
      series: [
        {
          type: "pie",
          // labelKey: "docType",
          legendItemKey: "docType",
          angleKey: "count",
          sectorLabelKey: "count",
        },
      ],
      data: chartData,
    }),
    [chartData]
  );
  if (isEmpty(loanDocuments)) {
    return;
  }
  return <AgCharts options={chartOptions} />;
};

export default LoanDocumentGroupFilesCard;
